import { supabase } from '../supabaseClient';
import { isEventListed } from '../utils/eventType';

export const PROFILE_SELECT_OWN =
  'id, display_name, email, phone_number, profile_photo_url, instagram_handle, tiktok_handle, first_name, last_name, zip_code, avatar_hue, followers_count, following_count';
export const PROFILE_SELECT_PUBLIC =
  'id, display_name, profile_photo_url, instagram_handle, tiktok_handle, avatar_hue, followers_count, following_count';
/** Signed in, a profile also shows the person's real name under the display name (anon has no first/last: 164-165). */
export const PROFILE_SELECT_SIGNED_IN = `${PROFILE_SELECT_PUBLIC}, first_name, last_name`;
export const PROFILE_EVENTS_SELECT =
  'event_id, event_name, display_name, display_name_override, event_datetime, event_date, ends_at, multi_day, status, event_type, festival_ready, hero_image_url, card_image_url, genre, going_count, venues(name, city, venue_tier)';

export async function fetchLiveFollowCounts(targetUserId) {
  if (!targetUserId) return { followers_count: 0, following_count: 0 };
  const [{ count: followersCount }, { count: followingCount }] = await Promise.all([
    supabase
      .from('follows')
      .select('follower_id', { count: 'exact', head: true })
      .eq('following_id', targetUserId),
    supabase
      .from('follows')
      .select('following_id', { count: 'exact', head: true })
      .eq('follower_id', targetUserId),
  ]);
  return {
    followers_count: followersCount ?? 0,
    following_count: followingCount ?? 0,
  };
}

export async function fetchProfileBundle({ profileUserId, viewerUserId }) {
  const isOwn = Boolean(viewerUserId && profileUserId === viewerUserId);
  const isOther = Boolean(viewerUserId && profileUserId !== viewerUserId);

  const iBlockThemPromise = isOther
    ? supabase
        .from('user_blocks')
        .select('blocker_id')
        .eq('blocker_id', viewerUserId)
        .eq('blocked_user_id', profileUserId)
        .maybeSingle()
    : Promise.resolve({ data: null });
  const theyBlockMePromise = isOther
    ? supabase
        .from('user_blocks')
        .select('blocker_id')
        .eq('blocker_id', profileUserId)
        .eq('blocked_user_id', viewerUserId)
        .maybeSingle()
    : Promise.resolve({ data: null });
  const theyFollowMePromise = isOther
    ? supabase
        .from('follows')
        .select('follower_id')
        .eq('follower_id', profileUserId)
        .eq('following_id', viewerUserId)
        .maybeSingle()
    : Promise.resolve({ data: null });

  const [profileRes, followRes, liveCounts, iBlockRes, theyBlockRes, reverseFollowRes] =
    await Promise.all([
      isOwn
        ? supabase.rpc('get_my_profile').select(PROFILE_SELECT_OWN).maybeSingle()
        : supabase
            .from('profiles')
            .select(viewerUserId ? PROFILE_SELECT_SIGNED_IN : PROFILE_SELECT_PUBLIC)
            .eq('id', profileUserId)
            .maybeSingle(),
      isOther
        ? supabase
            .from('follows')
            .select('follower_id')
            .eq('follower_id', viewerUserId)
            .eq('following_id', profileUserId)
            .maybeSingle()
        : Promise.resolve({ data: null }),
      fetchLiveFollowCounts(profileUserId),
      iBlockThemPromise,
      theyBlockMePromise,
      theyFollowMePromise,
    ]);

  if (profileRes.error) throw profileRes.error;

  return {
    profile: profileRes.data ? { ...profileRes.data, ...liveCounts } : null,
    isFollowing: Boolean(followRes.data),
    iBlockedThem: Boolean(iBlockRes.data),
    theyFollowMe: Boolean(reverseFollowRes.data),
    theyBlockedMe: Boolean(theyBlockRes.data),
  };
}

export async function fetchProfileEvents(profileUserId) {
  const { data: rsvps } = await supabase
    .from('rsvps')
    .select('event_id, status')
    .eq('user_id', profileUserId);

  const eventIds = [...new Set((rsvps ?? []).map((r) => r.event_id))];
  if (eventIds.length === 0) {
    return { userRsvps: rsvps ?? [], eventsMap: {} };
  }

  const [{ data: events }, { data: allRsvps }] = await Promise.all([
    supabase.from('events').select(PROFILE_EVENTS_SELECT).in('event_id', eventIds),
    supabase.from('rsvps').select('event_id, status').in('event_id', eventIds),
  ]);

  const rsvpCountsByEvent = {};
  (allRsvps ?? []).forEach((row) => {
    if (!rsvpCountsByEvent[row.event_id]) rsvpCountsByEvent[row.event_id] = { going: 0 };
    if (row.status === 'going') rsvpCountsByEvent[row.event_id].going += 1;
  });

  const eventsMap = {};
  // A festival the feed hides never files under Going or Past: its event page would say "Event not found".
  (events ?? []).filter(isEventListed).forEach((ev) => {
    eventsMap[ev.event_id] = {
      ...ev,
      going_count: rsvpCountsByEvent[ev.event_id]?.going ?? ev.going_count ?? 0,
    };
  });

  return { userRsvps: (rsvps ?? []).filter((r) => eventsMap[r.event_id]), eventsMap };
}

export async function fetchViewerRsvpsForEvents(viewerUserId, eventIds) {
  if (!viewerUserId || !eventIds?.length) return {};
  const { data } = await supabase
    .from('rsvps')
    .select('event_id, status')
    .eq('user_id', viewerUserId)
    .in('event_id', eventIds);
  const map = {};
  (data ?? []).forEach((r) => {
    map[r.event_id] = r.status;
  });
  return map;
}
